import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    LinearProgress,
    Typography,
    Slide,
    Paper,
} from "@mui/material";
import { GridSortDirection } from "@mui/x-data-grid-pro";
import { PrintOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { UIContext } from "../../../context/UIContext";
import { dateRange } from "../../../helpers/dateUtils";
import useBulkExport from "../../../helpers/useBulkExport";
import { TransactionFiltersContext } from "../../../context/TransactionFiltersContext";
import { PrintContext } from "../context/PrintContext";
import StatusFileIcon from "./StatusFileIcon";
import { percentageFormat } from "../../../helpers/helpers";
import { UserContext } from "../../../context/UserContext";


export interface ExportBottomBarProps {
    sortBy: string;
    sortOrder: GridSortDirection;
    isAdmin?: boolean;
    search?: string;
}

const ProgressWithPercentage = (props: { progress: number | undefined }) => {

    const { progress } = props;
    const { locale } = useContext(UserContext);

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinearProgress
                variant={progress !== undefined ? "determinate" : "indeterminate"}
                value={progress}
                sx={{ flex: 1, height: 6, borderRadius: 1 }}
            />

            <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                {percentageFormat((progress ?? 0) / 100, locale)}
            </Typography>
        </Box>
    );
};

export const ExportBottomBar = ({
    sortBy,
    sortOrder,
    isAdmin,
    search,
}: ExportBottomBarProps) => {

    const { t } = useTranslation();
    const { colorMode } = useContext(UIContext);
    const { filters, dateRangeConfig } = useContext(TransactionFiltersContext);
    const { isPrinting, setIsPrinting, reportType, progress: detailsProgress } =
        useContext(PrintContext);

    const { locale } = useContext(UserContext);

    const [filename, setFilename] = useState<string | undefined>();
    const [progress, setProgress] = useState<number>();

    const hasDownloadedRef = useRef(false);

    const { startDate, endDate } = dateRange(
        filters.period,
        filters.periodFrom,
        filters.periodTo,
        undefined,
        dateRangeConfig
    );

    useEffect(() => {
        if (isPrinting && !filename) {
            setFilename(`Transaction ${reportType}.pdf`);
        }
    }, [isPrinting, reportType, filename]);

    const { progress: summaryProgress, fileUri, error } = useBulkExport({
        startDate,
        endDate,
        sortBy,
        sortOrder: sortOrder as string,
        filename,
        reportType,
        isAdmin,
        location: filters.location?.id,
        locationArea: filters.locationArea?.id,
        staff: filters.staff?.id,
        device: filters.device?.id,
        paymentMethod: filters.paymentMethod?.id,
        search: search || undefined,
        companyGuid: filters.companyGuid,
        hasActiveFlag: filters.hasActiveFlag,
        activeFlagCategory: filters.activeFlagCategory,
        format: "pdf",
    });

    useEffect(() => {

        if (summaryProgress !== undefined) {
            setProgress(summaryProgress);

            if (summaryProgress === 100) {
                setIsPrinting(false);
            }
        }

    }, [summaryProgress, setIsPrinting]);

    useEffect(() => {

        if (detailsProgress !== undefined) {
            setProgress(detailsProgress);

            if (detailsProgress === 100) {
                setIsPrinting(false);
            }
        }

    }, [detailsProgress, setIsPrinting]);

    useEffect(() => {

    if (!fileUri) return;
    if (hasDownloadedRef.current) return;

    hasDownloadedRef.current = true;

    const link = document.createElement("a");
    link.href = fileUri;
    link.download = filename || "export.pdf";

    document.body.appendChild(link);

    link.click(); 

    document.body.removeChild(link);

    setTimeout(() => {
        URL.revokeObjectURL(fileUri);
        hasDownloadedRef.current = false;
    }, 15000);

}, [fileUri, filename]);

    return (
        <Slide direction="up" in={isPrinting} mountOnEnter unmountOnExit>

            <Paper
                elevation={6}
                sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    zIndex: (theme) => theme.zIndex.appBar - 1,
                    backgroundColor: (theme) =>
                        colorMode === "light"
                            ? theme.palette.background.paper
                            : theme.palette.background.default,
                }}
            >

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        flex: 1,
                        gap: 0.75,
                        p: 1,
                    }}
                >

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                        <PrintOutlined
                            sx={{ fontSize: 22, color: "text.secondary" }}
                        />

                        <Typography variant="eventTitle">
                            {t("export.printingTitle")}
                        </Typography>

                    </Box>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ marginLeft: "28px" }}
                    >
                        {t("export.printingSubtitle")}
                    </Typography>

                </Box>

                <Box display="flex" alignItems="center" gap={2}>

                    <Card
                        variant="outlined"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            minWidth: 300,
                            width: "100%",
                        }}
                    >

                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: 1,
                            }}
                        >

                            <Box
                                sx={{
                                    display: "flex",
                                    marginBottom: "18px",
                                    textAlign: "left",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >

                                <StatusFileIcon
                                    fileUri={fileUri}
                                    error={error}
                                />

                                <Typography variant="body2">
                                    {filename}
                                </Typography>

                            </Box>

                            <ProgressWithPercentage progress={progress} />

                        </Box>

                        <Button
                            variant="text"
                            size="medium"
                            onClick={() => setIsPrinting(false)}
                            sx={{
                                ml: 2,
                                alignSelf: "center",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {t("actions.cancel")}
                        </Button>

                    </Card>

                </Box>

            </Paper>

        </Slide>
    );
};
